import { MessageSquareWarning } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/core/components/ui/sheet";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useIsMobile } from "@/core/component-hooks/use-mobile";
import type { StatementItemReference } from "@/payroll/ai-script/statement/statement-renderer";
import { PACK_QUESTION_DESCRIPTION } from "./pack-question-copy";
import { PackQuestionForm } from "./pack-question-form";

interface AiScriptTechnicianFeedbackFormProps {
  businessId: string | null;
  runId: string;
  attached: StatementItemReference | null;
  onClearAttached: () => void;
  /** Sent, so the caller can take them to the conversation it started. */
  onSent?: (threadId: string) => void;
  aiEnabled?: boolean;
}

/**
 * The technician's way in to asking about their PayPack, with an optional figure.
 *
 * What they have already asked lives in its own tab; this is only the asking,
 * so it sits at the foot of the pack where the figures are.
 *
 * On a phone the form is a bottom drawer. Inline, it sits below every block of
 * the statement — on a real pack that is several screens down, and focusing it
 * dragged the technician the whole length of their own pay, leaving the figure
 * they tapped thousands of pixels above the fold. The drawer rises over the
 * statement instead, so the figure stays where they left it.
 *
 * On a desktop the pack is far shorter in screens and there is no keyboard
 * eating the viewport, so the form stays inline and needs no dismissing.
 */
export const AiScriptTechnicianFeedbackForm = ({
  businessId,
  runId,
  attached,
  onClearAttached,
  onSent,
  aiEnabled = false,
}: AiScriptTechnicianFeedbackFormProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  // `useIsMobile` reports false until its effect runs, so a phone rendered the
  // inline desktop form for a frame before swapping to the drawer trigger.
  // Waiting one commit avoids mounting, then discarding, a form with its own
  // state.
  const [breakpointKnown, setBreakpointKnown] = useState(false);
  useEffect(() => setBreakpointKnown(true), []);

  // Tapping a flag anywhere in the statement is what opens the drawer — the
  // technician has already said what they are asking about.
  useEffect(() => {
    if (attached && isMobile) setOpen(true);
  }, [attached, isMobile]);

  const renderForm = ({
    autoFocus = false,
    heading = true,
  }: {
    autoFocus?: boolean;
    heading?: boolean;
  } = {}) => (
    <PackQuestionForm
      businessId={businessId}
      runId={runId}
      aiEnabled={aiEnabled}
      attached={attached}
      onClearAttached={onClearAttached}
      onSent={(threadId) => {
        setOpen(false);
        onSent?.(threadId);
      }}
      autoFocus={autoFocus}
      heading={heading}
    />
  );

  // One commit of nothing rather than the desktop form flashing on a phone.
  if (!breakpointKnown) return null;

  if (!isMobile) {
    return (
      <Stack
        fullWidth
        gap="lg"
        className="border-t border-stroke-tertiary pt-5"
      >
        {renderForm()}
      </Stack>
    );
  }

  return (
    <Stack fullWidth gap="lg" className="border-t border-stroke-tertiary pt-5">
      <Stack fullWidth gap="xs">
        <Button
          variant="tertiary"
          fullWidth
          onClick={() => setOpen(true)}
          leftIcon={<MessageSquareWarning className="h-4 w-4" />}
        >
          Something look wrong?
        </Button>
        <Typography variant="bodyW3" textColor="tertiary">
          Or tap the flag beside any figure above to ask about it directly.
        </Typography>
      </Stack>

      <Sheet
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // Dismissing without sending should release the figure, or its flag
          // stays marked with nothing on screen to explain why.
          if (!next) onClearAttached();
        }}
      >
        <SheetContent
          side="bottom"
          // The keyboard takes roughly half a phone screen, so cap the height
          // and let the content scroll inside rather than under it.
          className="max-h-[85dvh] overflow-y-auto rounded-t-xl"
          // Radix would focus its close button; the form focuses the box itself.
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <SheetHeader className="mb-4 pr-8 text-left">
            <SheetTitle>Something look wrong?</SheetTitle>
            <SheetDescription className="text-secondary-text">
              {aiEnabled
                ? PACK_QUESTION_DESCRIPTION.ai
                : PACK_QUESTION_DESCRIPTION.manager}
            </SheetDescription>
          </SheetHeader>
          {renderForm({ autoFocus: true, heading: false })}
        </SheetContent>
      </Sheet>
    </Stack>
  );
};
